import { useCallback, useEffect, useRef, useState } from 'react';
import { Input, Spin, Typography, message } from 'antd';
import type { InputRef } from 'antd';
import { BarcodeOutlined } from '@ant-design/icons';
import { useSerialTrackingEnabled } from '@/hooks/useGeneralConfig';
import { useQrVariantSearch } from '../hooks/useQrVariantSearch';
import { usePosCartAdder } from '../hooks/usePosCartAdder';
import { QRSuggestItem } from './QRSuggestItem';
import { QRComboSuggestItem } from './QRComboSuggestItem';
import type {
  FulfillmentMode,
  PosComboSearchItem,
  PosVariant,
  PosVariantDetailItem,
  PosVariantSearchItem,
} from '../types';

interface Props {
  fulfillmentMode: FulfillmentMode;
  onVariantFound: (variant: PosVariant) => void;
  /**
   * Tuỳ chọn — vắng mặt ở màn KHÔNG hỗ trợ combo (ví dụ sửa đơn,
   * `Orders/edit.tsx`, ngoài phạm vi tính năng combo POS). Khi vắng mặt, toàn
   * bộ luồng combo (nhóm "Combo" trong dropdown, phím Enter chọn combo, khớp
   * đúng mã combo khi quét) bị tắt hẳn — không chỉ ẩn UI mà còn không gọi
   * lookup/onComboFound, để màn đó giữ nguyên hành vi cũ y hệt trước khi có
   * combo.
   */
  onComboFound?: (combo: PosComboSearchItem, details: PosVariantDetailItem[]) => void;
  /**
   * TCR-1126 review round 2 — tuỳ chọn, mặc định `false`. Chỉ màn POS
   * (`pages/POS/index.tsx`) truyền `true` để lọc kết quả theo kênh POS
   * (`is_sellable_pos`). Sửa đơn (`Orders/edit.tsx`) dùng chung component này
   * nhưng KHÔNG truyền — đơn online/web có sản phẩm chưa bật kênh POS, siết
   * sẽ khiến nhân viên không sửa được đơn.
   */
  posChannelOnly?: boolean;
}

export function QRSearchBar({
  fulfillmentMode,
  onVariantFound,
  onComboFound,
  posChannelOnly,
}: Props) {
  const [qr, setQr] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);
  const prevSearchingRef = useRef(false);

  // `hasResults` từ hook tính CẢ `results.combos`, chưa gate theo
  // `onComboFound` — bỏ qua, dùng `hasAnyResult` cục bộ bên dưới thay thế.
  const { results, isSearching } = useQrVariantSearch(qr, fulfillmentMode, posChannelOnly);
  const { enabled: serialTrackingEnabled } = useSerialTrackingEnabled();

  // Không có `onComboFound` (màn không hỗ trợ combo) → coi như không có combo
  // nào cả, ở MỌI nơi đọc kết quả tìm kiếm (render, Enter, quét mã).
  const comboResults = onComboFound ? results.combos : [];

  // Ở màn không hỗ trợ combo (`onComboFound` vắng mặt), một từ khoá CHỈ khớp
  // combo sẽ khiến `hasResults` thô của hook là `true` trong khi `comboResults`
  // (đã gate) rỗng — dropdown mở ra không có nhóm nào để vẽ, hệ quả là một
  // khung trắng thay vì thông báo "không tìm thấy". MỌI nơi cần biết "có kết
  // quả để hiện hay không" phải dùng biến CỤC BỘ này, không phải `hasResults`
  // thô của hook.
  const hasAnyResult =
    results.serials.length > 0 || results.variants.length > 0 || comboResults.length > 0;

  // Effect 1: open/close results dropdown based on hasAnyResult
  useEffect(() => {
    if (hasAnyResult) {
      setShowResults(true);
      setShowNoResults(false);
    } else {
      setShowResults(false);
    }
  }, [hasAnyResult]);

  // Effect 2: show "no results" once searching transitions to done
  useEffect(() => {
    if (prevSearchingRef.current && !isSearching && !hasAnyResult && qr.trim().length > 0) {
      setShowNoResults(true);
    }
    if (isSearching) setShowNoResults(false);
    prevSearchingRef.current = isSearching;
  }, [isSearching, hasAnyResult, qr]);

  // Effect 3: click outside closes both dropdowns
  useEffect(() => {
    if (!showResults && !showNoResults) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setShowResults(false);
        setShowNoResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showResults, showNoResults]);

  // Effect 4: F3 focus + Escape close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'F3') {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === 'Escape') {
        setShowResults(false);
        setShowNoResults(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleAdded = useCallback(() => {
    setQr('');
    setShowResults(false);
    setShowNoResults(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const { isAdding, addByQrCode, selectCombo } = usePosCartAdder({
    fulfillmentMode,
    onVariantFound,
    onComboFound,
    onAdded: handleAdded,
  });

  const selectItem = useCallback(
    async (item: PosVariantSearchItem) => {
      const qrValue = item.serial
        ? `${item.variant.variant_code}_${item.serial}`
        : item.variant.variant_code;
      await addByQrCode(qrValue);
    },
    [addByQrCode],
  );

  const handleEnter = useCallback(async () => {
    if (isAdding) return;
    const trimmed = qr.trim();
    if (!trimmed) return;

    if (showResults && hasAnyResult) {
      // TCR-1046 (tiếp) — combo giờ có thể khớp qua SẢN PHẨM THÀNH PHẦN, không
      // chỉ tên/sku của chính combo (xem ComboPosSearchService.searchForPos ở
      // BE). Trước đây combo chỉ khớp khi search đúng combo nên ưu tiên combo
      // vô điều kiện là an toàn; giờ phải ưu tiên sản phẩm/serial KHỚP CHÍNH
      // XÁC variant_code trước — mirror đúng guard "Khớp CHÍNH XÁC" đã có ở
      // usePosCartAdder.addByQrCode. Không làm vậy: gõ tay mã 1 sản phẩm lẻ
      // (thành phần của combo nào đó) rồi Enter sẽ add nhầm CẢ combo thay vì
      // đúng sản phẩm lẻ đó — hỏng đơn thật.
      const lowerTrimmed = trimmed.toLowerCase();
      const exactMatch = [...results.serials, ...results.variants].find(
        (item) => item.variant.variant_code.toLowerCase() === lowerTrimmed,
      );
      if (exactMatch) {
        await selectItem(exactMatch);
        return;
      }
      const firstCombo = comboResults[0];
      if (firstCombo) {
        await selectCombo(firstCombo);
        return;
      }
      const first = results.serials[0] ?? results.variants[0];
      if (first) {
        await selectItem(first);
        return;
      }
    }
    if (isSearching) {
      void message.info('Đang tìm kiếm, vui lòng đợi…');
      return;
    }
    await addByQrCode(trimmed);
  }, [
    isAdding,
    qr,
    showResults,
    hasAnyResult,
    results,
    comboResults,
    isSearching,
    selectCombo,
    selectItem,
    addByQrCode,
  ]);

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1400,
    marginTop: 4,
    background: '#fff',
    border: '1px solid #f0f0f0',
    borderRadius: 4,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <Input
        ref={inputRef}
        data-testid="pos-qr-search-input"
        prefix={<BarcodeOutlined />}
        placeholder={
          serialTrackingEnabled
            ? 'QR / Mã SP / Barcode... (F3)'
            : 'Mã SP / Barcode... (F3)'
        }
        value={qr}
        onChange={(e) => setQr(e.target.value)}
        onPressEnter={() => void handleEnter()}
        suffix={isSearching || isAdding ? <Spin size="small" /> : null}
        allowClear
        autoComplete="off"
      />

      {showNoResults && !hasAnyResult && !isSearching && (
        <div style={{ ...dropdownStyle, padding: '12px 16px' }}>
          <Typography.Text type="secondary">Không tìm thấy sản phẩm phù hợp</Typography.Text>
        </div>
      )}

      {showResults && hasAnyResult && (
        <div
          style={{ ...dropdownStyle, maxHeight: 400, overflow: 'auto' }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {comboResults.length > 0 && (
            <>
              <div style={{ padding: '8px 12px', background: '#fafafa', fontWeight: 600, fontSize: 13 }}>
                Combo
              </div>
              {comboResults.map((combo) => (
                <QRComboSuggestItem
                  key={`combo-${combo.combo_id}`}
                  item={combo}
                  fulfillmentMode={fulfillmentMode}
                  onClick={() => void selectCombo(combo)}
                />
              ))}
            </>
          )}
          {results.serials.length > 0 && (
            <>
              <div style={{ padding: '8px 12px', background: '#fafafa', fontWeight: 600, fontSize: 13 }}>
                Serials
              </div>
              {results.serials.map((it) => (
                <QRSuggestItem
                  key={`serial-${it.variant.variant_id}-${it.serial ?? ''}`}
                  item={it}
                  onClick={() => void selectItem(it)}
                />
              ))}
            </>
          )}
          {results.variants.length > 0 && (
            <>
              <div style={{ padding: '8px 12px', background: '#fafafa', fontWeight: 600, fontSize: 13 }}>
                Variants
              </div>
              {results.variants.map((it) => (
                <QRSuggestItem
                  key={`variant-${it.variant.variant_id}`}
                  item={it}
                  onClick={() => void selectItem(it)}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
